// Prepositions — the most-used ones with their core meanings, the famous
// por-vs-para split, and the "personal a". A frequency-ordered reference table
// of the most common prepositions sits at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const MOST_USED = [
  ["de", "of / from", "el libro de Ana · soy de Perú"],
  ["a", "to / at", "voy a casa · a las dos"],
  ["en", "in / on / at", "en la mesa · en Madrid"],
  ["con", "with", "café con leche"],
  ["por", "by / through / because of", "por la mañana · gracias por todo"],
  ["para", "for / in order to", "para ti · para aprender"],
  ["sin", "without", "sin azúcar"],
  ["sobre", "on / about", "sobre la mesa · un libro sobre arte"],
  ["entre", "between / among", "entre tú y yo"],
  ["hasta", "until / up to", "hasta mañana"],
  ["desde", "since / from", "desde ayer"],
  ["hacia", "toward", "hacia el norte"],
  ["contra", "against", "contra la pared"],
  ["según", "according to", "según ella"],
  ["durante", "during", "durante la clase"],
];

export function prepositionsView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Most-Used Prepositions</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Spanish</th><th class="py-2 px-3">English</th><th class="py-2 px-3">Example</th>
        </tr></thead>
        <tbody>
          ${MOST_USED
            .map(
              ([es, en, ex]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
            <td class="py-2 px-3 text-sage-500 italic">${ex}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  renderGuideSections(
    app,
    "Prepositions",
    "Prepositions link words together — showing place, time, direction, or relationship. Spanish prepositions rarely map one-to-one onto English, so it pays to learn them in context rather than by single-word translation.",
    [
      {
        title: "1. por vs para — the big one",
        body: `
          <p class="text-sage-700 mb-2">Both can translate as <i>for</i>, but they split cleanly once you see the logic.</p>
          <p class="text-sage-700 text-sm mb-1"><b>por</b> looks <i>backward</i> at cause/means — reason, exchange, duration, movement <i>through</i>:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-2">
            <li><i>Gracias <b>por</b> la ayuda</i> (cause) · <i>Pagué 10 € <b>por</b> esto</i> (exchange)</li>
            <li><i>Estudié <b>por</b> dos horas</i> (duration) · <i>Camino <b>por</b> el parque</i> (through)</li>
          </ul>
          <p class="text-sage-700 text-sm mb-1"><b>para</b> looks <i>forward</i> at purpose/destination — goal, recipient, deadline:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Estudio <b>para</b> aprender</i> (purpose) · <i>Es <b>para</b> ti</i> (recipient)</li>
            <li><i><b>Para</b> el viernes</i> (deadline) · <i>Salgo <b>para</b> Madrid</i> (destination)</li>
          </ul>
        `,
      },
      {
        title: "2. The personal a",
        body: `
          <p class="text-sage-700 mb-2">When the direct object of a verb is a <b>specific person</b> (or a pet), Spanish inserts an <b>a</b> with no English equivalent.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Veo <b>a</b> María.</i> (I see María.) — but <i>Veo la casa</i> (a thing, no <i>a</i>).</li>
            <li><i>Busco <b>a</b> mi hermano.</i></li>
            <li>Skip it after <i>tener</i>: <i>Tengo dos hermanos.</i></li>
          </ul>
        `,
      },
      {
        title: "3. Verbs that come glued to a preposition",
        body: `
          <p class="text-sage-700 mb-2">Many verbs take a fixed preposition you simply memorise with the verb:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>pensar <b>en</b></i> (think about) · <i>soñar <b>con</b></i> (dream of)</li>
            <li><i>depender <b>de</b></i> (depend on) · <i>aprender <b>a</b></i> (learn to)</li>
            <li><i>casarse <b>con</b></i> (marry) · <i>insistir <b>en</b></i> (insist on)</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3">After a preposition, a verb stays in the <b>infinitive</b>: <i>antes de <b>salir</b></i> (before leaving), <i>sin <b>pensar</b></i> (without thinking).</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
